/**
 * External dependencies
 */
import React, { useEffect } from 'react';
import { connect } from 'react-redux';

/**
 * Internal dependencies
 */
import { requestCurrentSupportIfNotRequested } from 'state/current-support/actions';
import { getCurrentSupport, requestingCurrentSupport } from 'state/current-support/selectors';
import SupportPlanLoader from './support-plan-loader';
import Money from 'components/money';
import PlanName from 'components/plan-name';
import DateTime from 'components/date-time';
import Coupon from 'components/coupon';
import { Stack, Text } from '@towersystems/towersystems-design-system';
import DetailListItem from 'components/detail-list-item';
import SubscriptionStatus from 'components/subscription-status';
import Card from 'components/card';

/**
 * Style dependencies
 */
import './styles.scss';

const CurrentSupportPlanCard = (props) => {
    const { currentSupport, requesting } = props;

    useEffect(() => {
        props.requestCurrentSupport();
    }, []);

    if (!currentSupport && requesting) {
        return <SupportPlanLoader />;
    }

    if (!currentSupport) {
        return (
            <Card>
                <Card.Header heading='Plan details' />
                <Stack>
                    <Card.Section>
                        <DetailListItem label='Plan amount' />
                    </Card.Section>
                    <Card.Section>
                        <DetailListItem label='Billing period' />
                    </Card.Section>
                    <Card.Section>
                        <DetailListItem label='Next billing date' />
                    </Card.Section>
                </Stack>
            </Card>
        );
    }

    return (
        <Card>
            <Card.Header heading={<PlanName value={currentSupport.plan_id} />} />
            <Stack dividers>
                <Card.Section>
                    <DetailListItem
                        label='Status'
                        detail={<SubscriptionStatus status={currentSupport.status} />}
                    />
                </Card.Section>
                <Card.Section>
                    <DetailListItem
                        label='Price'
                        detail={
                            <span>
                                <Money value={currentSupport.plan_unit_price / 100} /> / {currentSupport.billing_period} {currentSupport.billing_period_unit}
                            </span>
                        }
                    />
                </Card.Section>
                {currentSupport.status === 'active' && (
                    <Card.Section>
                        <DetailListItem
                            label='Next billing date'
                            detail={<DateTime date={currentSupport.next_billing_at} format='Do MMM, YYYY' />}
                        />
                    </Card.Section>
                )}
                {currentSupport.status === 'future' && (
                    <Card.Section>
                        <DetailListItem
                            label='Start date'
                            detail={<DateTime date={currentSupport.start_date} format='Do MMM, YYYY' />}
                        />
                    </Card.Section>
                )}
                {currentSupport.addons && currentSupport.addons.length > 0 && (
                    <Card.Section>
                        <DetailListItem
                            label='Add-ons'
                            detail={
                                <Stack space='small'>
                                    {currentSupport.addons.map(({ id, amount, quantity }) => (
                                        <Text key={id} size='small'>
                                            {id} — <Money value={amount / 100} /> (×{quantity})
                                        </Text>
                                    ))}
                                </Stack>
                            }
                        />
                    </Card.Section>
                )}
                {currentSupport.coupons && currentSupport.coupons.length > 0 && (
                    <Card.Section>
                        <DetailListItem
                            label='Discounts'
                            detail={
                                <Stack space='small'>
                                    {currentSupport.coupons.map(({ coupon_id }) => (
                                        <Coupon key={coupon_id} couponId={coupon_id} />
                                    ))}
                                </Stack>
                            }
                        />
                    </Card.Section>
                )}
            </Stack>
        </Card>
    );
};

export default connect(
    (state) => ({
        currentSupport: getCurrentSupport(state),
        requesting: requestingCurrentSupport(state),
    }),
    {
        requestCurrentSupport: requestCurrentSupportIfNotRequested,
    },
)(CurrentSupportPlanCard);
